/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Injectable } from '@angular/core'
import { AbstractControl, UntypedFormGroup } from '@angular/forms'

@Injectable({
  providedIn: 'root',
})
export class FormValidationService {
  /*
   * dynamic-forms doesn't trigger form validation on submit automatically, so we mark every
   * enabled control as touched (disabled / hidden controls are excluded so they don't flip the
   * group to invalid) and then focus the FIRST invalid control.
   *
   * The previous implementation focus+blur'd every control in a loop, which left focus on the
   * last invalid one — confusing for keyboard and screen-reader users who expect focus to land
   * on the first error per WCAG 3.3.1 (Error Identification) and 2.4.3 (Focus Order).
   */
  static triggerFormValidationErrors(formGroup: UntypedFormGroup): void {
    FormValidationService.markEnabledControlsAsTouched(formGroup)

    const firstInvalidName = FormValidationService.findFirstInvalidControlName(formGroup)
    if (!firstInvalidName) {
      return
    }

    const element = document.querySelector<HTMLElement>(`[name="${firstInvalidName}"]`)
    element?.focus()
  }

  private static markEnabledControlsAsTouched(formGroup: UntypedFormGroup): void {
    Object.values(formGroup.controls)
      .filter((ctrl) => !ctrl.disabled)
      .forEach((ctrl) => ctrl.markAllAsTouched())
  }

  private static findFirstInvalidControlName(formGroup: UntypedFormGroup): string | undefined {
    for (const [controlName, control] of Object.entries(formGroup.controls)) {
      if (control.disabled) {
        continue
      }

      if (FormValidationService.isFormGroup(controlName, control)) {
        const nestedName = FormValidationService.findFirstInvalidControlName(control)
        if (nestedName) {
          return nestedName
        }
      } else if (control.invalid) {
        return controlName
      }
    }

    return undefined
  }

  private static isFormGroup(controlName: string, control: AbstractControl): control is UntypedFormGroup {
    return controlName.toLowerCase().includes('group') && (control as UntypedFormGroup).controls !== undefined
  }
}
